import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Home, TreeDeciduous, Landmark, ScrollText, HeartHandshake, Database, GitBranch, Camera, BookOpen, PartyPopper, Users, Sparkles, Plane, Trophy, ShieldCheck, CalendarHeart, Share2, Hourglass, Infinity as InfinityIcon, ArrowRight, ChevronUp, ChevronDown } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

// All twenty Unique Value Propositions from the client brief.
// Each slide bundles a title, description AND the image so the right-hand card
// updates in lockstep with the left highlight when auto-advancing.
const UVP_SLIDES = [
    { id: "built-for-indian-families", icon: Home, title: "Built Specifically for Indian Families", desc: "Designed around Indian family structures, traditions, and relationships.", image_url: "/uvp/01-built-for-indian-families.webp" },
    { id: "preserve-history", icon: TreeDeciduous, title: "Preserve Family History Across Generations", desc: "Protects family information that is often lost within 2–3 generations.", image_url: "/uvp/02-preserve-history.webp" },
    { id: "cultural-heritage", icon: Landmark, title: "Record Indian Cultural Heritage", desc: "Captures family customs, traditions, festivals, and cultural practices.", image_url: "/uvp/03-cultural-heritage.webp" },
    { id: "gotra-lineage", icon: ScrollText, title: "Document Gotra, Kuldevata & Family Lineage", desc: "Supports uniquely Indian genealogical and spiritual identifiers.", image_url: "/uvp/04-gotra-lineage.webp" },
    { id: "values-wisdom", icon: HeartHandshake, title: "Preserve Family Values & Wisdom", desc: "Stores life lessons, principles, and guidance from elders.", image_url: "/uvp/05-values-wisdom.webp" },
    { id: "digital-heritage-repo", icon: Database, title: "Digital Family Heritage Repository", desc: "A single secure place for family history, memories, and records.", image_url: "/uvp/06-digital-heritage-repo.webp" },
    { id: "multi-gen-tree", icon: GitBranch, title: "Multi-Generational Family Tree", desc: "Connects past, present, and future generations in one platform.", image_url: "/uvp/07-multi-gen-tree.webp" },
    { id: "photos-videos", icon: Camera, title: "Store Family Photographs & Videos", desc: "Preserves visual memories alongside family relationships.", image_url: "/uvp/08-photos-videos.webp" },
    { id: "biography", icon: BookOpen, title: "Biography & Life Story Integration", desc: "Links family members with their biographies and life journeys.", image_url: "/uvp/09-biography.webp" },
    { id: "traditions", icon: PartyPopper, title: "Family Traditions Documentation", desc: "Records rituals, ceremonies, and important family practices.", image_url: "/uvp/10-traditions.webp" },
    { id: "joint-families", icon: Users, title: "Suitable for Joint & Extended Families", desc: "Designed to accommodate complex Indian family structures.", image_url: "/uvp/11-joint-families.webp" },
    { id: "inter-gen-bonds", icon: Sparkles, title: "Strengthen Inter-Generational Bonds", desc: "Helps younger generations understand their roots and heritage.", image_url: "/uvp/12-inter-gen-bonds.webp" },
    { id: "nri-families", icon: Plane, title: "Ideal for NRI Families", desc: "Keeps globally dispersed families connected to their origins.", image_url: "/uvp/13-nri-families.webp" },
    { id: "achievements", icon: Trophy, title: "Preserve Family Achievements", desc: "Documents accomplishments of family members across generations.", image_url: "/uvp/14-achievements.webp" },
    { id: "legacy-vault", icon: ShieldCheck, title: "Secure Digital Legacy Vault", desc: "Protects important family information for future access.", image_url: "/uvp/15-legacy-vault.webp" },
    { id: "milestones", icon: CalendarHeart, title: "Family Events & Milestones Archive", desc: "Records marriages, anniversaries, births, and significant occasions.", image_url: "/uvp/16-milestones.webp" },
    { id: "easy-sharing", icon: Share2, title: "Easy Sharing Within Family", desc: "Controlled access for family members across locations.", image_url: "/uvp/17-easy-sharing.webp" },
    { id: "long-term-legacy", icon: Hourglass, title: "Long-Term Legacy Preservation", desc: "Ensures family identity survives beyond individual lifetimes.", image_url: "/uvp/19-long-term-legacy.webp" },
    { id: "permanent-legacy", icon: InfinityIcon, title: "Create a Permanent Family Legacy", desc: "Transforms scattered memories into an enduring family heritage asset.", image_url: "/uvp/20-permanent-legacy.webp" },
];
const AUTO_MS = 5000;

export const WhyFamiliesChooseUs = () => {
    const { t } = useLang();
    const slides = UVP_SLIDES;
    const [idx, setIdx] = useState(0);
    const [dir, setDir] = useState(1); // 1 = advance (upward), -1 = go back (downward)
    const pauseUntil = useRef(0);
    const itemRefs = useRef([]);

    const go = (n, manual) => {
        setDir(n > idx ? 1 : -1);
        setIdx(((n % slides.length) + slides.length) % slides.length);
        if (manual) pauseUntil.current = Date.now() + 8000;
    };
    const advance = () => { setDir(1); setIdx((i) => (i + 1) % slides.length); };

    useEffect(() => {
        const timer = setInterval(() => { if (Date.now() > pauseUntil.current) advance(); }, AUTO_MS);
        return () => clearInterval(timer);
    }, []);

    // Keep the active UVP in view within the (scrollable) left list as auto-advance runs.
    // Manually scroll only the parent <ul>, never the window — prevents page jump.
    useEffect(() => {
        const el = itemRefs.current[idx];
        const list = el?.parentElement;
        if (!el || !list) return;
        // offsetTop is relative to nearest positioned ancestor; compute relative to the list itself
        // using getBoundingClientRect so scrolling behaves correctly regardless of surrounding layout.
        const elRect = el.getBoundingClientRect();
        const listRect = list.getBoundingClientRect();
        const relativeTop = elRect.top - listRect.top + list.scrollTop;
        const target = relativeTop - list.clientHeight / 2 + el.clientHeight / 2;
        list.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
    }, [idx]);

    // Vertical (upward) slide animation for both the image AND the text panel.
    const vSlide = {
        initial: (d) => ({ opacity: 0, y: d > 0 ? 40 : -40 }),
        animate: { opacity: 1, y: 0 },
        exit: (d) => ({ opacity: 0, y: d > 0 ? -40 : 40 }),
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
    };

    const slide = slides[idx];

    return (
        <section id="why-families" className="py-6 md:py-8 bg-white lg:min-h-[calc(100svh-72px)] lg:flex lg:items-center" data-testid="landing-why-families">
            <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EF4444]">{t("wcuEyebrow")}</p>
                    <h2 className="mt-1 font-extrabold tracking-tight text-[#6D28D9] leading-[1.08] whitespace-nowrap" style={{ fontSize: "clamp(14px, 3.2vw, 30px)" }} data-testid="wcu-heading">{t("wcuHeading")}</h2>
                </div>

                <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                    {/* Left column — all UVPs list, active auto-scrolls into view, list scrolls internally */}
                    <ul
                        className="lg:col-span-6 space-y-1.5 max-h-[440px] overflow-y-auto pr-1 uvp-list scrollbar-hide"
                        data-testid="why-families-benefits"
                    >
                        {slides.map((b, i) => {
                            const active = i === idx;
                            const Icon = b.icon;
                            return (
                                <motion.li
                                    key={b.id}
                                    ref={(el) => { itemRefs.current[i] = el; }}
                                    initial={{ opacity: 0, x: -16 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.5) }}
                                    className={`flex items-start gap-2.5 rounded-xl p-2 cursor-pointer transition-all ${active ? "bg-[#F4F3FF] border border-[#DDD6FE] shadow-sm" : "border border-transparent hover:bg-[#FAFAFF]"}`}
                                    onClick={() => go(i, true)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") go(i, true); }}
                                    data-testid={`why-families-benefit-${i + 1}`}
                                    aria-current={active ? "true" : "false"}
                                >
                                    <span className={`h-8 w-8 shrink-0 rounded-lg inline-flex items-center justify-center transition-colors ${active ? "bg-[#6D28D9] text-white" : "bg-[#FEE2E2] text-[#EF4444]"}`}>
                                        <Icon className="h-3.5 w-3.5" />
                                    </span>
                                    <div className="min-w-0">
                                        <h3 className="text-[13.5px] font-bold text-[#1F2937] leading-tight">{b.title}</h3>
                                        <p className="mt-0.5 text-[11.5px] leading-snug text-[#4B5563]">{b.desc}</p>
                                    </div>
                                </motion.li>
                            );
                        })}
                    </ul>

                    {/* Right column — auto-scrolling image + synced text (vertical upward) */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-6 rounded-2xl bg-[#F4F3FF] border border-[#EDE9FE] p-4 sm:p-4 shadow-[0_16px_44px_rgba(76,29,149,0.10)]"
                        data-testid="why-families-card"
                    >
                        {/* Image window — clipped so slide-up animation stays contained */}
                        <div className="relative w-full aspect-[3/2] overflow-hidden rounded-xl bg-[#EDE9FE]" data-testid="why-families-image-window">
                            <AnimatePresence mode="wait" custom={dir}>
                                <motion.img
                                    key={slide.id + "-img"}
                                    src={slide.image_url}
                                    alt={slide.title}
                                    loading="lazy"
                                    custom={dir}
                                    variants={vSlide}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={vSlide.transition}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    data-testid={`why-families-photo-${idx}`}
                                />
                            </AnimatePresence>
                        </div>

                        {/* Slider controls — vertical arrows + progress bar */}
                        <div className="mt-3 flex items-center justify-between" data-testid="why-families-slider-controls">
                            <button
                                type="button"
                                onClick={() => go(idx - 1, true)}
                                aria-label="Previous"
                                data-testid="why-families-prev"
                                className="h-8 w-8 rounded-full bg-white border border-[#EDE9FE] text-[#6D28D9] inline-flex items-center justify-center hover:bg-[#EDE9FE]"
                            >
                                <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <div className="flex-1 mx-3 flex flex-col gap-1" data-testid="why-families-progress">
                                <div className="text-[10.5px] font-semibold text-[#6D28D9] tracking-wide tabular-nums text-center">
                                    {String(idx + 1).padStart(2, "0")} <span className="text-[#A78BFA]">/ {String(slides.length).padStart(2, "0")}</span>
                                </div>
                                <div className="h-1 rounded-full bg-[#EDE9FE] overflow-hidden">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] rounded-full"
                                        animate={{ width: `${((idx + 1) / slides.length) * 100}%` }}
                                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                        data-testid="why-families-progress-fill"
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => go(idx + 1, true)}
                                aria-label="Next"
                                data-testid="why-families-next"
                                className="h-8 w-8 rounded-full bg-white border border-[#EDE9FE] text-[#6D28D9] inline-flex items-center justify-center hover:bg-[#EDE9FE]"
                            >
                                <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-3 flex justify-center lg:justify-end">
                    <Link
                        to="/signup"
                        data-testid="why-families-cta-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white px-6 py-2.5 text-[13.5px] font-semibold shadow-[0_10px_24px_rgba(109,40,217,0.30)] transition-all hover:-translate-y-0.5"
                    >
                        {t("wcuCtaT")} <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
};
